import { IACError } from '../iacError';
import { TYPES } from '../constants';
import { IACProtocol, IACMessageData } from './iacProtocol';
import { IACUsecase } from '../iacType/iacUsecase';
import { IACEvent } from '../iacType/iacEvent';
import { Config, Navigator } from '..';
import { IACSendingInformation } from '../application/baseApplication';
import * as Logger from '@navigator/frontend-logger';

/*
 * Defines, if the transportation layer is already initialized and can be used.
 */
let initialized = false;

/*
 * IACTransport
 */
export class IACTransport {

  /*
   * onMessage callback function.
   */

  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
  public onMessage(message: IACUsecase): void {}

  /*
   * Initializes the transportation layer
   */
  public init(): void {
    window.addEventListener('message', (evt) => this.onMessageWrapper(evt), false);

    initialized = true;
  }

  /*
   * Returns, if the layer is initialized, or not.
   */
  public initialized(): boolean {
    return initialized;
  }

  /*
   * Sends a message to the related target.
   */
  public send(messageData: IACMessageData, target: IACSendingInformation): void {
    const data = this.outgoingMessageFactory(messageData, target);

    (target ? target.contentWindow : top).postMessage(
      JSON.stringify(data), '*',
    );
  }

  /*
   * Creates a valid message object. The passed data should implement
   * the IACMessageData Interface, but old clients sends for the 'type'
   * a string instead of a bitwise flag.
   *
   * @param {object} data -Message data
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public incomingMessageFactory(data: any): IACUsecase | IACEvent {
    let message;
    const protocol = new IACProtocol();

    // We protect the IAC from third party json messages
    if (!this.validateMessageData(data)) {
      return null;
    }

    // IAC 3.x.x to IAC 4.0.0 normalization
    switch (data.type) {
      case 'RPC':
        data.type = TYPES.USECASE | TYPES.RPC | TYPES.INTERN;
        this.setIACVersion(data.sender, 310);
        break;
      case 'RPE':
        data.type = TYPES.USECASE | TYPES.RPE | TYPES.INTERN;
        this.setIACVersion(data.sender, 310);
        break;
      case 'HUB':
        data.type = TYPES.USECASE | TYPES.RPE | TYPES.INTERN;
        this.setIACVersion(data.sender, 310);
        break;
      default:
        this.setIACVersion(data.sender, 400);
        break;
    }

    protocol.setFlags(data.type);
    protocol.setName(data.usecase);
    protocol.setSender(data.sender);
    protocol.setPayload.apply(protocol, data.args);

    switch (true) {

      case !!(TYPES.USECASE & data.type):
        message = new IACUsecase();
        // This message is a bit special,
        // because the navigator supports a config object, regarding the use ucase.
        message.config = Config.get().communication.usecases[data.usecase];
        break;

      case !!(TYPES.EVENT & data.type):
        message = new IACEvent();
        break;

      default:
        throw new IACError('Invalid IAC message detected, which is not flagged as USECASE or EVENT.');
    }

    message.protocol = protocol;
    message.transport = this;

    return message;
  }

  /*
   * Outgoing message factory. Normally the result should be the same
   * as the income which implements IACSendingInformation. But older IAC
   * clients only support a string as 'type'
   *
   * @param data
   * @param target
   * @returns {mixed}
   */
  public outgoingMessageFactory(data: IACMessageData, target: IACSendingInformation): any {
    const copiedData: any = Object.assign({}, data);

    switch (true) {

      case (target.iacVersion === 300 || target.iacVersion === 310):

        // Downgrading the IAC 4 message for IAC 3.x.x
        if (TYPES.EVENT & data.type) {
          copiedData.type = 'EVT';

        } else {
          copiedData.type = (TYPES.RPC & data.type ? 'RPC' : 'RPE');
        }

        break;

      case (target.iacVersion === 400):
        // IAC 4 clients are always welcome.
        // There is nothing to do.
        break;

      default:
        // Older or unknown IAC clients are no longer supported.
        throw new IACError(`The application "${target.iacAppname}" is using` +
          ` an unknown or deprecated IAC version: ${target.iacVersion}`);
    }

    return copiedData;
  }

  /*
   * Wraps all incoming messages
   *
   * @param {MessageEvent} event -The post message event
   */
  private onMessageWrapper(event: MessageEvent) {
    if (typeof event.data !== 'string') {
      return;
    }

    let data;

    try {
      data = JSON.parse(event.data);
    } catch (e) {
      Logger.warn(
        'Could not parse post message data.',
        e,
      );
    } finally {
      if (data) {
        this.onMessage(
          this.incomingMessageFactory(data) as IACUsecase,
        );
      }
    }

  }

  /*
   * Validates the messaging data, to protect the IAC from third party json messages.
   * @param {Object} data contains the messaging data
   * @returns {Boolean}
   */
  private validateMessageData(data: IACMessageData): boolean {
    return !!(data && data.sender && data.type && data.usecase);
  }

  /*
   * Sets the current IAC version of the sending application.
   * This information is needed for incoming and outgoing messages.
   * @param {String} sender
   * @param {Number} version
   */
  private setIACVersion(sender: string, version: number) {
    const app = Navigator.getApplication(sender);
    if (app) {
      app.iacVersion = version;
    } else {
      Logger.error(`IACTransport.setIACVersion: No initialized app '${sender}' found!`);
    }
  }
}
